import { reportError } from "utils/diagnostics";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FieldValues, useForm } from "react-hook-form";
import { FieldError, StyledTextField } from "components/common/Input";
import { Popup } from "components/common/popup/popup.component";
import { Spinner } from "components/common/spinner/spinner.component";
import { CommonSelect } from "components/common/common-select/common-select.component";
import { showError, showSuccess } from "utils/notifications";
import { getFirmwareUploadUrl, registerSoftwareVersion, uploadFirmwareToS3 } from "./manage-versions.api";

const MODEL_OPTIONS = [
  { value: "MegaFi-2", label: "MegaFi-2" },
  { value: "MegaFi-1", label: "MegaFi-1" },
  { value: "AW-12", label: "AW-12" },
  { value: "Modem", label: "Modem (FN990A40)" },
];

const MODEM_BAND_OPTIONS = [
  { value: "B14", label: "Band 14" },
  { value: "B28", label: "Band 28" },
];

interface RegisterVersionDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: () => void;
}

const RegisterVersionDialog = ({ open, onClose, onAdd }: RegisterVersionDialogProps): JSX.Element => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [allowDownload, setAllowDownload] = useState<boolean>(true);
  const [allowVpn, setAllowVpn] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  }: FieldValues = useForm({ defaultValues: { model: "MegaFi-2", version: "", targetBand: "B14", sha256: "" } });
  const isModemFirmware = watch("model") === "Modem";

  const onRegister = async ({ model, version, targetBand, sha256 }: Record<string, string>) => {
    if (!file) {
      setFileError("A firmware .bin file is required");
      return;
    }

    setIsSaving(true);
    try {
      const { uploadUrl } = await getFirmwareUploadUrl(file.name);

      try {
        await uploadFirmwareToS3(uploadUrl, file);
      } catch {
        showError("Firmware upload to S3 failed");
        return;
      }

      await registerSoftwareVersion({
        model,
        version: isModemFirmware ? "" : version.trim(),
        fileName: file.name,
        ...(isModemFirmware ? { targetBand } : {}),
        sha256: (sha256 || "").trim(),
        allowDownload,
        allowVpn,
      });

      showSuccess(isModemFirmware ? "Modem firmware registered" : "Software version registered");
      onAdd();
      onClose();
    } catch (error) {
      reportError("software.register.onRegister", error);
      // upload-url / register errors are surfaced by the axios interceptor
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popup
      title="Register Firmware"
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      primaryBtn={{ text: "Register", onClick: handleSubmit(onRegister), disabled: isSaving }}
      secondaryBtn={{ text: "Cancel", onClick: onClose }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <CommonSelect
          name="model"
          label="Model"
          options={MODEL_OPTIONS}
          control={control}
          rules={{ required: "Model is required" }}
          error={errors.model}
        />

        {isModemFirmware ? (
          <CommonSelect
            name="targetBand"
            label="Target band"
            options={MODEM_BAND_OPTIONS}
            control={control}
            rules={{ required: "Target band is required" }}
            error={errors.targetBand}
          />
        ) : (
          <>
            <StyledTextField
              label="Version (e.g. v3.7.0.18)"
              {...register("version", {
                required: "Version is required",
                maxLength: { value: 20, message: "Version is too long" },
                pattern: {
                  value: /^[A-Za-z0-9.-]+$/,
                  message: "Only letters, numbers, dot and dash (no model suffix)",
                },
              })}
              error={errors.version}
            />
            {errors.version && <FieldError>{errors.version.message}</FieldError>}
          </>
        )}

        <input
          type="file"
          accept=".bin"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setFileError("");
          }}
          style={{ marginTop: "8px" }}
        />
        {fileError && <FieldError>{fileError}</FieldError>}

        <StyledTextField
          label="SHA256 (optional)"
          {...register("sha256", {
            pattern: {
              value: /^[A-Fa-f0-9]{64}$/,
              message: "SHA256 must be 64 hex characters",
            },
          })}
          error={errors.sha256}
        />
        {errors.sha256 && <FieldError>{errors.sha256.message}</FieldError>}

        {!isModemFirmware && (
          <>
            <FormControlLabel
              control={<Switch checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} />}
              label="Downloadable"
            />
            <FormControlLabel
              control={<Switch checked={allowVpn} onChange={(e) => setAllowVpn(e.target.checked)} />}
              label="VPN allowed"
            />
          </>
        )}
      </Box>

      <Popup title="Registering version..." open={isSaving}>
        <Spinner />
      </Popup>
    </Popup>
  );
};

export default RegisterVersionDialog;
